import { useState } from "react";
import {
  unresolvedTies,
  type Command,
  type StandingTie,
  type Tournament,
} from "@spikeeasy/tournament";
import { Dialog, Notice, teamName } from "./ui";

export function TieResolution({
  t,
  send,
  busy,
}: {
  t: Tournament;
  send: (command: Command) => Promise<void>;
  busy: boolean;
}) {
  const ties = unresolvedTies(t);
  const [choice, setChoice] = useState<{
    tie: StandingTie;
    winnerId: string;
  } | null>(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const confirm = async () => {
    if (!choice) return;
    setError("");
    try {
      await send({
        type: "resolve_tie",
        key: choice.tie.key,
        winnerId: choice.winnerId,
      });
      setMessage(`${teamName(t, choice.winnerId)} wins the tie.`);
      setChoice(null);
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <details open={ties.length > 0}>
      <summary>
        Resolve tied standings{ties.length > 0 ? ` (${ties.length})` : ""}
      </summary>
      <div className="stack">
        {message && <Notice>{message}</Notice>}
        {ties.length === 0 ? (
          <p>
            No ties need a decision. Head-to-head results settle ties
            automatically where possible. Any remaining ties appear here when
            their pool or stage is complete.
          </p>
        ) : (
          <>
            <p>
              These ties remain after the match results and tie-breakers. Choose
              the team that takes the next place.
            </p>
            {ties.map((tie) => (
              <section className="tie-decision stack" key={tie.key}>
                <h3>
                  {tie.label} · Place {tie.place}
                </h3>
                <div className="tie-options">
                  {tie.teamIds.map((id) => (
                    <button
                      className="secondary"
                      key={id}
                      disabled={busy}
                      onClick={() => {
                        setError("");
                        setChoice({ tie, winnerId: id });
                      }}
                    >
                      {teamName(t, id)}
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </>
        )}
      </div>
      {choice && (
        <Dialog
          title="Confirm tie winner"
          onClose={() => {
            if (!busy) setChoice(null);
          }}
        >
          <div className="stack">
            <p>
              <strong>{teamName(t, choice.winnerId)}</strong> takes place{" "}
              {choice.tie.place} in {choice.tie.label}.
            </p>
            <p>
              The standings will update. If this settles qualification, the
              playoff draw will be generated.
            </p>
            {error && <Notice error>{error}</Notice>}
            <div className="actions">
              <button
                className="secondary"
                disabled={busy}
                onClick={() => setChoice(null)}
              >
                Cancel
              </button>
              <button className="primary" disabled={busy} onClick={confirm}>
                {busy ? "Saving…" : "Confirm winner"}
              </button>
            </div>
          </div>
        </Dialog>
      )}
    </details>
  );
}
